import { useLayoutEffect, useRef } from "react";

import closedArt from "./art/case-closed.webp";
import openArt from "./art/case-open.webp";
import classes from "./case.module.css";
import { LID_AT_MS, OPENING, type Part } from "./opening";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/**
 * Created.ai's Theme Case (spec web/case-art), Daniil's art of it closed and open (spec
 * web/case-art-images), where the reel will be. `opening` plays its beats: it shakes, the closed art
 * gives way to the open one (`onLid`, for the latch's sound) and light pours out; `onOpened` says
 * they are done. With reduced motion, or a browser without the Web Animations API, it plays none and
 * is opened at once. The closed art is the image a screen reader names "Theme Case"; the open one is
 * decoration. `data-theme-case` is its hook, `data-beats` marks the beats.
 */
export function ThemeCase({ opening, onLid, onOpened }: { opening: boolean; onLid: () => void; onOpened: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const closed = useRef<HTMLImageElement>(null);
  const open = useRef<HTMLImageElement>(null);
  const light = useRef<HTMLSpanElement>(null);
  // The latest callbacks, set before the opening's effect below runs.
  const handlers = useRef({ onLid, onOpened });
  useLayoutEffect(() => {
    handlers.current = { onLid, onOpened };
  });

  useLayoutEffect(() => {
    const parts: Record<Part, HTMLElement | null> = {
      case: box.current,
      closed: closed.current,
      open: open.current,
      light: light.current,
    };
    const whole = parts.case;
    if (!opening || whole === null) return;
    if (reducedMotion() || typeof whole.animate !== "function") {
      handlers.current.onOpened();
      return;
    }
    whole.dataset.beats = "";
    const played = OPENING.flatMap(({ part, keyframes, delay, duration, easing }) => {
      const element = parts[part];
      return element === null ? [] : [element.animate(keyframes, { delay, duration, easing, fill: "forwards" })];
    });
    const latch = window.setTimeout(() => handlers.current.onLid(), LID_AT_MS);
    // Cancelled beats (the case closed again, or gone) reject: nothing more to say then.
    Promise.all(played.map((beat) => beat.finished)).then(
      () => handlers.current.onOpened(),
      () => undefined,
    );
    return () => {
      window.clearTimeout(latch);
      for (const beat of played) beat.cancel();
      delete whole.dataset.beats;
    };
  }, [opening]);

  return (
    <div className={classes.caseStage}>
      {/* The showcase: faint rays turning slowly, and a glow under the floating case (spec web/case-polish). */}
      <span className={classes.stageRays} />
      <span className={classes.caseFloor} />
      <div className={classes.caseFloat}>
        <div className={classes.themeCase} ref={box} data-theme-case>
          <span className={classes.caseLight} ref={light} />
          <img className={classes.caseArt} ref={open} src={openArt} alt="" width={480} height={420} data-art="open" />
          <img className={classes.caseArt} ref={closed} src={closedArt} alt="Theme Case" width={480} height={420} data-art="closed" />
        </div>
      </div>
    </div>
  );
}
